/* eslint-disable no-restricted-globals */
import { useCallback, useEffect, useMemo, useState } from "react";
import { axios } from "@root/libs/axios";
import { csrfToken } from "@root/shared/utils/csrf";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";

type UseMultiScoutInfoParams = {
  addUrl: string;
  delUrl: string;
  addArrayUrl: string;
  delArrayUrl: string;
  resetUrl: string;
  checkedUserCount: number;
  countOfDefaultCheckedUserOnPage: number;
  userStatusArray: UserProps[];
  setUserStatusArray: React.Dispatch<React.SetStateAction<UserProps[] | undefined>>;
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
};

type ReturnType = {
  isMultiScoutChecked: boolean;
  updateSelectedUser: (value: string) => void;
  updateAllUser: () => void;
  resetAllUser: () => void;
  scoutUserCount: number;
};

type UserProps = {
  userId: string;
  isChecked: boolean;
};

const STATUS_OK = 200;
const STATUS_OVER_LIMIT = 422;

// レスポンスにはnumber（サーバ側の選択人数）も含まれるが、SPはローカル再計算のため未使用
type Response = {
  status: number;
};

type CalcScoutUserCountParams = {
  checkedUserCount: number;
  isReset: boolean;
  countOfDefaultCheckedUserOnPage: number;
  userStatusArray: UserProps[];
};

type PostUserStatusOptions = {
  authenticityToken: string;
  userId?: string;
  targetUserIdArray?: string[];
};

export const useMultiScoutInfo = ({
  addUrl,
  delUrl,
  addArrayUrl,
  delArrayUrl,
  resetUrl,
  checkedUserCount,
  countOfDefaultCheckedUserOnPage,
  userStatusArray,
  setUserStatusArray,
  setIsDisabled,
}: UseMultiScoutInfoParams): ReturnType => {
  const authenticityToken = useMemo(() => csrfToken(), []);
  const [isMultiScoutChecked, setIsMultiScoutChecked] = useState(false);
  const [isReset, setIsReset] = useState(false);

  const [scoutUserCount, setScoutUserCount] = useState(
    calcScoutUserCount({
      checkedUserCount,
      isReset,
      countOfDefaultCheckedUserOnPage,
      userStatusArray,
    }),
  );

  useEffect(() => {
    setScoutUserCount(
      calcScoutUserCount({
        checkedUserCount,
        isReset,
        countOfDefaultCheckedUserOnPage,
        userStatusArray,
      }),
    );
  }, [checkedUserCount, countOfDefaultCheckedUserOnPage, isReset, userStatusArray]);

  // ユーザのチェック状況を更新する関数
  // urlにはapiのurl、optionsにはauthenticity_tokenと、更新するユーザーID（単数もしくは複数）を指定します
  // 失敗時は行のチェック状態を元に戻したうえでfalseを返す（呼び出し元の追加ロールバック用）
  const postUserStatus = useCallback(
    async (url: string, options: PostUserStatusOptions): Promise<boolean> => {
      try {
        const { data } = await axios.post<Response>(url, options);
        if (data.status === STATUS_OVER_LIMIT) {
          alert("選択できませんでした。\n一括スカウトの上限は500件です。");
          setUserStatusArray(userStatusArray);
          return false;
        }
        if (data.status !== STATUS_OK) {
          alert("予期せぬエラーが発生しました。チェック状態を戻します。");
          setUserStatusArray(userStatusArray);
          return false;
        }
        return true;
      } catch (error) {
        // AxiosErrorをそのまま渡し、sendErrorLog内の通信断（ERR_NETWORK）除外を効かせる
        sendErrorLog(error as Error);
        alert("通信に失敗しました。チェック状態を戻します。");
        setUserStatusArray(userStatusArray);
        return false;
      }
    },
    [userStatusArray, setUserStatusArray],
  );

  // 単一のユーザーのチェック情報を更新する関数
  // 対象のチェック情報に応じて削除もしくは追加用のAPIに送信します
  const updateSelectedUser = useCallback(
    (value: string) => {
      const target = userStatusArray.find((userStatus) => String(userStatus.userId) === value);
      if (!target) return;
      setIsDisabled(true);
      postUserStatus(target.isChecked ? delUrl : addUrl, {
        authenticityToken,
        userId: target.userId,
      }).finally(() => setIsDisabled(false));
      setUserStatusArray(
        userStatusArray.map((userStatus) =>
          Number(userStatus.userId) === Number(value)
            ? {
                ...userStatus,
                isChecked: !target.isChecked,
              }
            : userStatus,
        ),
      );
    },
    [
      postUserStatus,
      addUrl,
      delUrl,
      authenticityToken,
      setUserStatusArray,
      userStatusArray,
      setIsDisabled,
    ],
  );

  // 現在表示されている全てのユーザーのチェック情報を一括で更新する関数
  // チェック状況に応じて一括でチェックする/チェックを外すAPIに送信します
  const updateAllUser = useCallback(() => {
    const targetArray = userStatusArray.map((userStatus) => userStatus.userId);
    setIsDisabled(true);
    postUserStatus(isMultiScoutChecked ? delArrayUrl : addArrayUrl, {
      authenticityToken,
      targetUserIdArray: targetArray,
    })
      .then((isSucceeded) => {
        // 失敗時は行のチェック状態と合わせて「全選択」チェックボックスも元に戻す
        if (!isSucceeded) setIsMultiScoutChecked(isMultiScoutChecked);
      })
      .finally(() => setIsDisabled(false));
    setUserStatusArray(
      userStatusArray.map((userStatus) => ({
        ...userStatus,
        isChecked: !isMultiScoutChecked,
      })),
    );
    setIsMultiScoutChecked(!isMultiScoutChecked);
  }, [
    postUserStatus,
    addArrayUrl,
    delArrayUrl,
    isMultiScoutChecked,
    authenticityToken,
    setUserStatusArray,
    userStatusArray,
    setIsDisabled,
  ]);

  // 全てのユーザーのチェックを一括で解除する関数
  const resetAllUser = useCallback(() => {
    if (
      !confirm(
        "本当に一括スカウトのチェックを解除しますか？\n(他のページでチェックしたスカウトも解除されます。)",
      )
    ) {
      return;
    }

    setIsDisabled(true);
    postUserStatus(resetUrl, {
      authenticityToken,
    })
      .then((isSucceeded) => {
        // 失敗時は「全選択」チェックボックスと他ページ分のカウント（isReset）も元に戻す
        if (!isSucceeded) {
          setIsMultiScoutChecked(isMultiScoutChecked);
          setIsReset(isReset);
        }
      })
      .finally(() => setIsDisabled(false));
    setUserStatusArray(userStatusArray.map((userStatus) => ({ ...userStatus, isChecked: false })));
    setIsMultiScoutChecked(false);
    setIsReset(true);
  }, [
    userStatusArray,
    postUserStatus,
    resetUrl,
    authenticityToken,
    isMultiScoutChecked,
    isReset,
    setUserStatusArray,
    setIsReset,
    setIsDisabled,
  ]);

  return {
    isMultiScoutChecked,
    updateSelectedUser,
    updateAllUser,
    resetAllUser,
    scoutUserCount,
  };
};

const calcScoutUserCount = ({
  checkedUserCount,
  isReset,
  countOfDefaultCheckedUserOnPage,
  userStatusArray,
}: CalcScoutUserCountParams): number => {
  const countOfCheckedUserOnOtherPage = isReset
    ? 0
    : checkedUserCount - countOfDefaultCheckedUserOnPage;
  const countOfCheckedUserOnPage = userStatusArray.filter((item) => item.isChecked).length;
  return countOfCheckedUserOnOtherPage + countOfCheckedUserOnPage;
};
